<template>
  <div class="min-h-screen bg-gray-50 py-8">
    <div class="max-w-7xl mx-auto px-6">
      <!-- Hero Section -->
      <div class="text-center mb-8">
        <h1 class="text-4xl font-bold text-gray-900 mb-4">About guest5of5</h1>
        <p class="text-xl text-gray-700 mb-8 leading-relaxed">
          Revolutionizing the hospitality experience through innovative technology and personalized service
        </p>
      </div>

      <!-- Content -->
      <div class="bg-white rounded-lg shadow-sm p-8">
        <!-- Mission Section -->
        <section class="mb-8">
          <div class="text-center mb-8">
            <div class="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i class="bi bi-bullseye text-2xl text-blue-600"></i>
            </div>
            <h2 class="text-2xl font-semibold text-gray-900 mb-4">Our Mission</h2>
            <p class="text-lg text-gray-700 leading-relaxed">
              To transform the way hotels and guests interact by providing a seamless, technology-driven platform 
              that enhances every aspect of the hospitality experience. We believe in making travel more 
              accessible, efficient, and enjoyable for everyone.
            </p>
          </div>
        </section>

        <!-- What We Do Section -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-6">What We Do</h2>
          
          <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <!-- Hotel Booking -->
            <div class="text-center p-6 bg-blue-50 rounded-xl">
              <div class="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i class="bi bi-building text-white text-xl"></i>
              </div>
              <h3 class="text-xl font-semibold text-gray-800 mb-3">Smart Hotel Booking</h3>
              <p class="text-gray-600">
                Advanced booking system with real-time availability, instant confirmations, and personalized recommendations.
              </p>
            </div>

            <!-- Pre-Checkin -->
            <div class="text-center p-6 bg-green-50 rounded-xl">
              <div class="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i class="bi bi-check-circle text-white text-xl"></i>
              </div>
              <h3 class="text-xl font-semibold text-gray-800 mb-3">Digital Pre-Checkin</h3>
              <p class="text-gray-600">
                Streamlined check-in process with digital forms, document uploads, and instant room assignments.
              </p>
            </div>

            <!-- Guest Experience -->
            <div class="text-center p-6 bg-purple-50 rounded-xl">
              <div class="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i class="bi bi-star text-white text-xl"></i>
              </div>
              <h3 class="text-xl font-semibold text-gray-800 mb-3">Enhanced Guest Experience</h3>
              <p class="text-gray-600">
                Personalized services, real-time communication, and seamless support throughout the stay.
              </p>
            </div>

            <!-- Management Tools -->
            <div class="text-center p-6 bg-orange-50 rounded-xl">
              <div class="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i class="bi bi-gear text-white text-xl"></i>
              </div>
              <h3 class="text-xl font-semibold text-gray-800 mb-3">Hotel Management</h3>
              <p class="text-gray-600">
                Comprehensive tools for hoteliers to manage bookings, rooms, and guest services efficiently.
              </p>
            </div>

            <!-- Analytics -->
            <div class="text-center p-6 bg-indigo-50 rounded-xl">
              <div class="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i class="bi bi-graph-up text-white text-xl"></i>
              </div>
              <h3 class="text-xl font-semibold text-gray-800 mb-3">Analytics & Insights</h3>
              <p class="text-gray-600">
                Detailed reports and analytics to help hotels optimize operations and improve guest satisfaction.
              </p>
            </div>

            <!-- Support -->
            <div class="text-center p-6 bg-teal-50 rounded-xl">
              <div class="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i class="bi bi-headset text-white text-xl"></i>
              </div>
              <h3 class="text-xl font-semibold text-gray-800 mb-3">24/7 Support</h3>
              <p class="text-gray-600">
                Round-the-clock customer support to ensure smooth operations and guest satisfaction.
              </p>
            </div>
          </div>
        </section>

        <!-- Why Choose Us Section -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-6">Why Choose Us</h2>
          
          <div class="grid md:grid-cols-2 gap-8">
            <!-- Innovation -->
            <div class="flex items-start space-x-4">
              <div class="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <i class="bi bi-lightbulb text-white text-xl"></i>
              </div>
              <div>
                <h3 class="text-xl font-semibold text-gray-800 mb-2">Innovation First</h3>
                <p class="text-gray-600">
                  We constantly innovate to bring cutting-edge technology to the hospitality industry, 
                  ensuring our platform stays ahead of the curve.
                </p>
              </div>
            </div>

            <!-- Reliability -->
            <div class="flex items-start space-x-4">
              <div class="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                <i class="bi bi-shield-check text-white text-xl"></i>
              </div>
              <div>
                <h3 class="text-xl font-semibold text-gray-800 mb-2">Reliable & Secure</h3>
                <p class="text-gray-600">
                  Our platform is built with enterprise-grade security and reliability, ensuring 
                  your data and operations are always protected.
                </p>
              </div>
            </div>

            <!-- Customer Focus -->
            <div class="flex items-start space-x-4">
              <div class="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                <i class="bi bi-heart text-white text-xl"></i>
              </div>
              <div>
                <h3 class="text-xl font-semibold text-gray-800 mb-2">Customer Focused</h3>
                <p class="text-gray-600">
                  Every feature is designed with the customer experience in mind, making travel 
                  more enjoyable and hassle-free.
                </p>
              </div>
            </div>

            <!-- Global Reach -->
            <div class="flex items-start space-x-4">
              <div class="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center flex-shrink-0">
                <i class="bi bi-globe text-white text-xl"></i>
              </div>
              <div>
                <h3 class="text-xl font-semibold text-gray-800 mb-2">Global Reach</h3>
                <p class="text-gray-600">
                  Our platform serves hotels and guests worldwide, providing consistent quality 
                  and service across all regions.
                </p>
              </div>
            </div>
          </div>
        </section>

        <!-- Contact CTA Section -->
        <section class="mb-8">
          <div class="text-center bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-8 text-white">
            <h2 class="text-2xl font-semibold mb-4">Ready to Transform Your Hospitality Experience?</h2>
            <p class="text-lg mb-6 opacity-90">
              Join thousands of hotels and guests who trust guest5of5 for their hospitality needs.
            </p>
            <div class="flex flex-col sm:flex-row gap-4 justify-center">
              <router-link 
                to="/contact" 
                class="px-6 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-gray-100 transition"
              >
                Get Started Today
              </router-link>
              <router-link 
                to="/hotels" 
                class="px-6 py-3 border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition"
              >
                Explore Hotels
              </router-link>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AboutUs',
  data() {
    return {
      // Add any data properties if needed
    }
  },
  methods: {
    // Add any methods if needed
  }
}
</script>

<style scoped>
/* Add any custom styles if needed */
</style> 